// react
import { useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';

// material ui
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

// shared components
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// components
import { formatHour } from '@/utils/formatters';

const ORDER = [
  { key: 'monday', label: 'Mandag' },
  { key: 'tuesday', label: 'Tirsdag' },
  { key: 'wednesday', label: 'Onsdag' },
  { key: 'thursday', label: 'Torsdag' },
  { key: 'friday', label: 'Fredag' },
  { key: 'saturday', label: 'Lørdag' },
  { key: 'sunday', label: 'Søndag' },
];

export default function OpeningHoursCard({
  title,
  location,
  description,
  openingHours,
}) {
  const { t } = useTranslation('energyxplorer-energy-overview-tab');
  const theme = useTheme();

  const items = useMemo(() => {
    if (!openingHours) return [];
    return ORDER.map(({ key, label }) => ({
      key,
      label,
      intervals: Array.isArray(openingHours[key]) ? openingHours[key] : [],
    }));
  }, [openingHours]);

  if (!openingHours) return null;

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {title}
            <InfoTooltipIcon title={description} />
          </Box>
        }
        subheader={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {location?.displayName}
            <PlaceRoundedIcon fontSize="small" />
          </Box>
        }
      />
      <CardContent>
        <Stack divider={<Divider flexItem />} spacing={0}>
          {items.map(({ key, label, intervals }) => {
            const hasIntervals = intervals.length > 0;

            return (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                <Typography sx={{ minWidth: 80, fontWeight: 500 }}>
                  {label}
                </Typography>

                {hasIntervals ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      py: 0.25,
                    }}
                  >
                    {intervals.map((it, idx) => {
                      const total = 24;

                      const startRaw =
                        typeof it?.start === 'number' ? it.start : 0;
                      const endRaw = typeof it?.end === 'number' ? it.end : 0;

                      const start = clamp(startRaw, 0, 24);
                      const end = clamp(endRaw, 0, 24);

                      const safeEnd = Math.max(end, start);

                      const leftPercent = (start / total) * 100;
                      const filledPercent = ((safeEnd - start) / total) * 100;
                      const rightPercent = 100 - leftPercent - filledPercent;

                      return (
                        <Box
                          key={`${key}-${idx}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ minWidth: 38 }}>
                            {formatHour(start)}
                          </Typography>

                          <Box
                            sx={{
                              position: 'relative',
                              height: 14,
                              width: '100%',
                              minWidth: 100,
                              borderRadius: 7,
                              background:
                                theme.palette.mode === 'light'
                                  ? theme.palette.grey[200]
                                  : theme.palette.grey[700],
                              overflow: 'hidden',
                              display: 'flex',
                            }}
                          >
                            {/* Empty before */}
                            <Box
                              sx={{ width: `${leftPercent}%`, height: '100%' }}
                            />
                            {/* Filled */}
                            <Box
                              sx={{
                                width: `${filledPercent}%`,
                                height: '100%',
                                background: theme.palette.primary.main,
                                transition: 'width 0.2s',
                              }}
                            />
                            {/* Empty after */}
                            <Box
                              sx={{ width: `${rightPercent}%`, height: '100%' }}
                            />
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{ minWidth: 38, textAlign: 'right' }}
                          >
                            {formatHour(safeEnd)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      color="text.secondary"
                      sx={{ fontStyle: 'italic' }}
                    >
                      {t('states.closed', { defaultValue: 'Lukket' })}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}
